import type { UiState } from "../../../state/editor/ui";
import { LEFT_RAIL, RIGHT_RAIL, isBasicLeftPanel } from "../rails/railConfig";

/**
 * Right-side panel ids that are not Advanced inspector tabs. They live in the
 * same `ui.rightTab` field so there is still exactly one open right panel —
 * the canvas overlay and the toolbar offset (`chromeInsets`) need no new case.
 */
export const BASIC_PANEL = "basic";
/** Retired as a right panel (AI Match now opens on the left, on Basic's rail); the
 * id survives so a saved `rightTab: "ai"` still resolves to something. */
export const AI_PANEL = "ai";

/** Advanced's "Pinned" panel — the settings a user starred, from any group. */
export const PINNED_PANEL = "pinned";

export function isAdvancedTab(id: string | null | undefined): boolean {
  return !!id && (id === PINNED_PANEL || RIGHT_RAIL.some((r) => r.id === id));
}

function isAdvancedLeftPanel(id: string | null | undefined): boolean {
  return !!id && LEFT_RAIL.some((r) => r.id === id);
}

/**
 * The ui patch for switching mode. Both sides follow the mode — the right
 * panel (Fine-tune ↔ the last Advanced tab) and the left one (Basic's presets
 * ↔ the last Advanced library) — so the switch always shows you something,
 * rather than flipping a rail and leaving a panel from the other mode open.
 */
export function switchModePatch(ui: UiState, mode: UiState["inspectorMode"]): Partial<UiState> {
  if (mode === "advanced") {
    const tab = isAdvancedTab(ui.lastAdvancedTab) ? ui.lastAdvancedTab : RIGHT_RAIL[0].id;
    const left = isAdvancedLeftPanel(ui.lastAdvancedLeftPanel) ? ui.lastAdvancedLeftPanel : null;
    return {
      inspectorMode: "advanced",
      rightTab: tab,
      lastRightTab: tab,
      leftPanel: left,
      ...(isBasicLeftPanel(ui.leftPanel) ? { lastBasicLeftPanel: ui.leftPanel } : {}),
    };
  }
  const left = isBasicLeftPanel(ui.lastBasicLeftPanel) ? ui.lastBasicLeftPanel : "scenes";
  return {
    inspectorMode: "basic",
    rightTab: BASIC_PANEL,
    lastRightTab: BASIC_PANEL,
    leftPanel: left,
    ...(isAdvancedTab(ui.rightTab) ? { lastAdvancedTab: ui.rightTab as string } : {}),
    ...(isAdvancedLeftPanel(ui.leftPanel) ? { lastAdvancedLeftPanel: ui.leftPanel } : {}),
  };
}

/** What the rail's show-panel chevron re-opens, valid for the current mode. */
export function reopenTarget(ui: UiState): string {
  const last = ui.lastRightTab;
  if (ui.inspectorMode === "basic") return BASIC_PANEL;
  return isAdvancedTab(last) ? (last as string) : isAdvancedTab(ui.lastAdvancedTab) ? ui.lastAdvancedTab : RIGHT_RAIL[0].id;
}
